// AI Service - Client-side API for AI features

export interface IChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export type AIProvider = 'ollama' | 'anthropic' | 'perplexity';

interface IChatResponse {
  reply: string;
  error?: string;
}

interface IRecommendResponse {
  recommendations: string;
  error?: string;
}

export interface IMediaItem {
  title: string;
  type: 'video' | 'image' | 'audio';
  source: string;
  category: string;
  yt?: boolean;
}

// Send chat message to AI
export async function sendChatMessage(
  messages: IChatMessage[],
  provider: AIProvider = 'ollama',
  context: string = ''
): Promise<string> {
  const response = await fetch('/api/ai/chat', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ messages, provider, context }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Network error' }));
    throw new Error(err.error || `AI service error: ${response.status}`);
  }

  const data: IChatResponse = await response.json();
  if (data.error) throw new Error(data.error);
  return data.reply;
}

// Get AI recommendations
export async function getAIRecommendations(
  mediaList: IMediaItem[],
  userQuery: string,
  provider: AIProvider = 'ollama'
): Promise<string> {
  const response = await fetch('/api/ai/recommend', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mediaList, userQuery, provider }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Network error' }));
    throw new Error(err.error || `AI service error: ${response.status}`);
  }

  const data: IRecommendResponse = await response.json();
  if (data.error) throw new Error(data.error);
  return data.recommendations;
}
